import cx from 'classnames';
import React, { memo, useEffect, useRef } from 'react';

import styles from './ParticleIcon.scss';

interface Vector2 {
  x: number;
  y: number;
}

interface ParticleInfo {
  position: Vector2; // normalized 0..1
  lifetime: number; // ms
  createdAt: number; // ms unix
  radius: number; // px
}

const defaultSizeOverLifetime = (liveFor: number) => {
  if (liveFor >= 1) {
    return 0;
  }

  const t = Math.max(0, Math.min(liveFor, 1));
  return (Math.cos(t * Math.PI) + 1) * 0.5;
};

const defaultVelocityOverLifetime = (liveFor: number) => {
  const speed = 2300;
  return { x: 0, y: (Math.cos(liveFor * Math.PI) + 1.0) / 2.0 / speed };
};

const CANVAS_SIZE = 30;

interface ParticleIconProps {
  className?: string;
  isCompact?: boolean;
  lifetime?: number; // ms
  lifetimeDelta?: number; // ms
  emitEveryMs?: number; // ms
  radius?: number;
  radiusDelta?: number;
  color?: string;
  padding?: number; // [0..0.5)
  sizeOverLifetime?: (liveFor: number) => number; // 0..1
  velocityOverLifetime?: (liveFor: number) => Vector2;
  children?: React.ReactNode;
}

const ParticleIcon: React.FC<ParticleIconProps> = ({
  className,
  isCompact = false,
  lifetime = 2500,
  lifetimeDelta = 1000,
  emitEveryMs = 500,
  radius = 1.2,
  radiusDelta = 0.3,
  color = '#f8e097',
  padding = 0.18,
  sizeOverLifetime = defaultSizeOverLifetime,
  velocityOverLifetime = defaultVelocityOverLifetime,
  children,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const particlesRef = useRef<ParticleInfo[]>([]);

  const rafIdRef = useRef<number | null>(null);
  const prevTsRef = useRef<number | null>(null);

  // Emitter accumulator (ms)
  const emitAccMsRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctxRef.current = ctx;

    const availableArea = 1 - 2 * padding;

    const createParticle = (nowMs: number) => {
      const horizontalPosition = padding + availableArea * Math.random();

      const p: ParticleInfo = {
        position: { x: horizontalPosition, y: 0 },
        lifetime: (Math.random() - 0.5) * lifetimeDelta + lifetime,
        createdAt: nowMs,
        radius: (Math.random() - 0.5) * radiusDelta + radius,
      };

      particlesRef.current.push(p);
    };

    const drawFrame = (ts: number) => {
      const ctx2d = ctxRef.current;
      if (!ctx2d) {
        return;
      }

      const nowMs = Date.now();

      const prevTs = prevTsRef.current;
      prevTsRef.current = ts;

      // delta in milliseconds
      const deltaMs = prevTs == null ? 16 : ts - prevTs;

      emitAccMsRef.current += deltaMs;
      if (emitAccMsRef.current >= emitEveryMs) {
        const count = Math.floor(emitAccMsRef.current / emitEveryMs);
        emitAccMsRef.current -= count * emitEveryMs;

        for (let i = 0; i < count; i += 1) {
          createParticle(nowMs);
        }
      }

      ctx2d.clearRect(0, 0, ctx2d.canvas.width, ctx2d.canvas.height);

      ctx2d.fillStyle = color;
      ctx2d.strokeStyle = color;
      ctx2d.beginPath();

      const particles = particlesRef.current;

      let write = 0;

      const w = ctx2d.canvas.width;
      const h = ctx2d.canvas.height;

      for (let i = 0; i < particles.length; i += 1) {
        const particle = particles[i];
        const livedFor = nowMs - particle.createdAt;

        if (livedFor > particle.lifetime) {
          continue;
        }

        const t = livedFor / particle.lifetime; // 0..1
        const v = velocityOverLifetime(t); // per-ms
        const s = sizeOverLifetime(t);

        const nextPos = {
          x: particle.position.x + v.x * deltaMs,
          y: particle.position.y + v.y * deltaMs,
        };

        particles[write] = particle;
        particles[write].position = nextPos;
        write += 1;

        const cx = nextPos.x * w;
        const cy = (1 - nextPos.y) * h;
        const r = particle.radius * s;

        ctx2d.moveTo(cx + r, cy);
        ctx2d.arc(cx, cy, r, 0, 2 * Math.PI, true);
      }

      particles.length = write;

      ctx2d.fill();
      ctx2d.stroke();

      rafIdRef.current = window.requestAnimationFrame(drawFrame);
    };

    rafIdRef.current = window.requestAnimationFrame(drawFrame);

    return () => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      prevTsRef.current = null;
      emitAccMsRef.current = 0;
      particlesRef.current = [];
      ctxRef.current = null;
    };
  }, [
    color,
    emitEveryMs,
    lifetime,
    lifetimeDelta,
    padding,
    radius,
    radiusDelta,
    sizeOverLifetime,
    velocityOverLifetime,
  ]);

  return (
    <div className={cx(styles.wrapper, className, isCompact && styles.compact)}>
      <canvas width={CANVAS_SIZE} height={CANVAS_SIZE} ref={canvasRef} />
      <div className={styles.content}>{children || <span className={styles.icon} />}</div>
    </div>
  );
};

export default memo(ParticleIcon);
